import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient, downloadBinary, fetchBlob, putBinary } from '@/lib/api';

/**
 * Opslag — de plattegronden van het terrein (docs/storage.md). Alles in
 * tekeningeenheden: de viewBox van de SVG, vanaf linksboven.
 */

/** The twelve colours of the categorical palette (`--cat-*`), in palette order. */
export const STATION_COLORS = [
  'blue',
  'petrol',
  'berry',
  'violet',
  'sky',
  'rose',
  'umber',
  'pine',
  'moss',
  'sand',
  'slate',
  'graphite',
] as const;
export type StationColor = (typeof STATION_COLORS)[number];

/**
 * The CSS for a type's colour. Every type has one, per owner (design_handoff_opslag/
 * eigenaren.md §1.3); null only for a row without a type, which stays an empty block.
 */
export const colorVar = (c: StationColor | string | null) =>
  c ? `var(--cat-${c})` : 'transparent';

/** The rule for a station's code, per owner (§21). */
export type StationCodeRule = 'ORDER_NUMBER' | 'NAME';

/**
 * Alfen's order or serial number: 8 to 30, in capitals. A name or batch as Greener
 * gives it (`Leticia_253`, `001`): up to 30, as typed. Neither has spaces. The same
 * numbers stand in the API (`services/api/src/modules/storage/storage.ts`).
 */
export const STATION_CODE_LIMITS: Record<StationCodeRule, { min: number; max: number }> = {
  ORDER_NUMBER: { min: 8, max: 30 },
  NAME: { min: 1, max: 30 },
};

/** What is stored for what was typed, as the API does it. */
export const stationCode = (raw: string, rule: StationCodeRule) =>
  rule === 'ORDER_NUMBER' ? raw.replace(/\s+/g, '').toUpperCase() : raw.replace(/\s+/g, '');

/** For whom Van Hooft stores (§21): a party, with what differs per owner. */
export interface StorageOwner {
  id: string;
  party: { id: string; code: string; name: string };
  /** For a file name: `stations-alfen.xlsx`. */
  slug: string;
  /** In a button, a heading and a sentence: `Greener`. */
  shortName: string;
  /** The letter in the head on its blocks; null is no head (Alfen, handoff `eigenaren.md §1`). */
  markLetter: string | null;
  codeRule: StationCodeRule;
  hasCustomers: boolean;
  /** Its relation in the WMS (`tbl_relati_cod`, owners step 8); null takes no part in the check. */
  wmsRelation: string | null;
  /** In the list: positions under that relation in the stock snapshot; null without either. */
  wmsPositions?: number | null;
  typeCount?: number;
  stationCount?: number;
}

/** Who a type or a station belongs to, as the screens show it. */
export interface OwnerRef {
  id: string;
  name: string;
  shortName: string;
  /** The letter in the head on its blocks; null is no head. */
  markLetter: string | null;
}

/** An article from TALIS a type stands for (§21.1, step 4), with what the form proposes. */
export interface StorageArticle {
  id: string;
  code: string;
  description: string;
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
  weightKg: number | null;
  wmsClient: string;
  isActive: boolean;
  suggestedName: string;
  suggestedAdr: string | null;
}

/** An UN number as VHT keeps it: `UN3536`. The API checks the same. */
export const UN_NUMBER = /^UN\d{4}$/;
export const unNumber = (raw: string) => raw.replace(/\s+/g, '').toUpperCase();

export interface StationType {
  id: string;
  name: string;
  colorToken: StationColor;
  owner: OwnerRef;
  /** The article in TALIS, with its sizes; null for a type without one. */
  article: StorageArticle | null;
  /** ADR: `UN3536`, or null when it is not dangerous goods. */
  adrUnNumber: string | null;
  stationCount?: number;
}

export interface Party {
  id: string;
  code: string;
  name: string;
}

export interface StorageStation {
  id: string;
  floorplanId: string;
  code: string;
  x: number;
  y: number;
  rotation: 0 | 90;
  /** The footprint as it stands: lying down swaps the two. */
  width: number;
  height: number;
  stationType: Pick<StationType, 'id' | 'name' | 'colorToken' | 'adrUnNumber'>;
  /** Always the type's owner. */
  owner: OwnerRef;
  /** ADR (§21.1, step 5), only for a type with an UN number. */
  adr: StationAdr | null;
  party: Party | null;
  movedAt: string | null;
  movedByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Floorplan {
  id: string;
  name: string;
  loadingPlace: {
    id: string;
    code: string;
    name: string | null;
    shortName: string | null;
    address: string | null;
  } | null;
  drawing: { name: string | null; bytes: number | null } | null;
  width: number | null;
  height: number | null;
  stationWidth: number;
  stationHeight: number;
  /** What a dragged station keeps from a neighbour it snaps beside or below. */
  stationGap: number;
  startX: number;
  startY: number;
  latitude: number | null;
  longitude: number | null;
  stationCount: number;
  stationsChangedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** On the overview: just enough per station to draw the miniature. */
export interface FloorplanSummary extends Floorplan {
  stations: {
    x: number;
    y: number;
    width: number;
    height: number;
    colorToken: StationColor;
    markLetter: string | null;
    /** Whose it is: the filter on an owner counts and dims by it (owners, step 7). */
    ownerId: string;
  }[];
}

/** How many on a plan are this owner's, for the overview with an owner chosen. */
export const ownedOn = (plan: Pick<FloorplanSummary, 'stations'>, ownerId: string) =>
  plan.stations.filter((s) => s.ownerId === ownerId).length;

export interface FloorplanDetail extends Floorplan {
  stations: StorageStation[];
}

export interface HistoryEvent {
  id: string;
  at: string;
  type: 'created' | 'moved' | 'rotated' | 'updated' | 'deleted' | 'migrated' | string;
  actorName: string | null;
  details: Record<string, unknown>;
}

export interface StationInput {
  code?: string;
  stationTypeId?: string;
  partyId?: string | null;
  adrLabelsOk?: boolean | null;
}

/** ADR on a battery: are the right labels on it (§21.1, step 5)? Null is not checked yet. */
export interface StationAdr {
  unNumber: string;
  labelsOk: boolean | null;
  checkedAt: string | null;
  checkedByName: string | null;
}

export type AdrState = 'ok' | 'notOk' | 'unchecked';
export const adrStateOf = (adr: StationAdr): AdrState =>
  adr.labelsOk === true ? 'ok' : adr.labelsOk === false ? 'notOk' : 'unchecked';

/** What the filter "ADR niet in orde" shows: not right, or not checked yet. */
export const adrNeedsAttention = (s: Pick<StorageStation, 'adr'>) =>
  s.adr !== null && s.adr.labelsOk !== true;

const BASE = '/api/v1/storage';

export const storageKeys = {
  floorplans: ['storage', 'floorplans'] as const,
  floorplan: (id: string) => ['storage', 'floorplan', id] as const,
  stationTypes: ['storage', 'station-types'] as const,
  owners: ['storage', 'owners'] as const,
  articles: (q: string) => ['storage', 'articles', q] as const,
  customers: ['storage', 'customers'] as const,
  history: (id: string) => ['storage', 'station', id, 'history'] as const,
  mapConfig: ['storage', 'map-config'] as const,
  /** Per owner chosen (its slug), or `all`. */
  dashboard: (owner = 'all') => ['storage', 'dashboard', owner] as const,
  movements: (grain: string, period: string, owner = 'all') =>
    ['storage', 'dashboard', 'movements', grain, period, owner] as const,
};

export const listFloorplans = () => apiClient.get<FloorplanSummary[]>(`${BASE}/floorplans`);
export const getFloorplan = (id: string) =>
  apiClient.get<FloorplanDetail>(`${BASE}/floorplans/${id}`);
export const listStationTypes = () => apiClient.get<StationType[]>(`${BASE}/station-types`);
export const listOwners = () => apiClient.get<StorageOwner[]>(`${BASE}/owners`);
/** At most 50 active articles from TALIS, on code, description or WMS client. */
export const listArticles = (q: string) =>
  apiClient.get<StorageArticle[]>(`${BASE}/articles`, { q });
export const listCustomers = () => apiClient.get<Party[]>(`${BASE}/customers`);
export const getStationHistory = (id: string) =>
  apiClient.get<HistoryEvent[]>(`${BASE}/stations/${id}/history`);

// ── Het dashboard (docs/storage.md §20) ─────────────────────────────────────

export type DashboardGrain = 'week' | 'month' | 'year';

export interface DashboardPoint {
  /** `2026-W44`, `2026-10` or `2026`. */
  key: string;
  start: string;
  end: string;
  stock: number;
  in: number;
  out: number;
}

/** The WMS history before the transition (§20, step 2): positions, not stations. */
export interface DashboardWmsPoint {
  key: string;
  start: string;
  /** The day the number is from: a Sunday, or the transition. */
  end: string;
  positions: number;
}

export interface DashboardCount {
  name: string | null;
  count: number;
}

export interface DashboardStation {
  id: string;
  code: string;
  plan: string;
  type: string;
  party: string | null;
}

/** An owner as the dashboard names it (owners, step 7). */
export interface DashboardOwner {
  id: string;
  slug: string;
  shortName: string;
}

export interface StorageDashboard {
  asOf: string;
  /** The owner chosen (owners, step 7), or null for all of them. */
  owner: (DashboardOwner & { hasCustomers: boolean; since: string | null }) | null;
  /** Whose the WMS history, the forecast and the inbound per week are: Alfen's. */
  wmsOwner: DashboardOwner | null;
  total: number;
  thisWeek: DashboardPoint | null;
  /** A type's colour and its owner's letter, for the block in the table. */
  byType: (DashboardCount & { colorToken: StationColor | null; markLetter: string | null })[];
  /** The KPI _ADR niet in orde_; null when no type has an UN number. */
  adr: { notOk: number; unchecked: number } | null;
  byPlan: DashboardCount[];
  byCustomer: DashboardCount[];
  stay: {
    count: number;
    mean: number | null;
    median: number | null;
    buckets: { key: string; count: number }[];
    longest: {
      code: string;
      plan: string;
      type: string;
      party: string | null;
      since: string;
      days: number;
      source: string;
    }[];
    fromTransition: number;
  };
  series: Record<DashboardGrain, DashboardPoint[]>;
  /** Null until the WMS history is loaded (`loadWmsHistory`). */
  wms:
    | (Record<DashboardGrain, DashboardWmsPoint[]> & {
        importedAt: string;
        transition: string;
        locations: string[];
      })
    | null;
  /**
   * Thirteen weeks ahead (§20, step 3); null without enough years of WMS history, and with
   * an owner chosen whose the WMS history is not.
   */
  forecast: DashboardForecast | null;
  /**
   * What came in per week, the last thirteen whole weeks (§20, step 4a): the WMS of Alfen up
   * to the transition, then VHT. Null when a week is missing.
   */
  inbound: DashboardInbound | null;
  /** For the invoice: the average in storage per day, per owner (owners, step 6). */
  billing: DashboardBilling;
  quality: {
    withoutCustomer: number;
    withoutCustomerStations: DashboardStation[];
    wmsSnapshotAt: string | null;
    notInWms: number | null;
    notInWmsStations: DashboardStation[];
    /**
     * In the WMS, but only under another relation than its owner's (owners, step 8). Null
     * without a snapshot, or when no owner in view has a relation.
     */
    otherRelation: number | null;
    otherRelationStations: DashboardOtherRelation[];
    /** The owners in view without a relation in the WMS: they take no part. */
    withoutRelation: DashboardOwner[];
  };
}

/** A station the WMS has under another relation, with whose it is on both sides. */
export interface DashboardOtherRelation extends DashboardStation {
  owner: DashboardOwner | null;
  found: { relation: string; owner: DashboardOwner | null }[];
}

/** A forecast point: the middle year, and the lowest and highest around it. */
export interface DashboardForecastPoint {
  key: string;
  start: string;
  /** The Sunday the number is for. */
  end: string;
  mid: number;
  low: number;
  high: number;
}

export interface DashboardForecast {
  /** The week it starts from, and what stands now. */
  from: { key: string; stock: number };
  /** Per earlier year: its own growth, and the change after thirteen weeks without it. */
  years: { year: number; growth: number; change: number }[];
  /** After thirteen weeks, as ratios (1.08 is +8%). */
  change: { mid: number; low: number; high: number };
  week: DashboardForecastPoint[];
  month: DashboardForecastPoint[];
  /** Whom it is about once there is more than one owner: Alfen, also with all of them. */
  about: DashboardOwner | null;
  /** The stock it starts from per period: Alfen's alone when `about` names it. */
  stock: Record<'week' | 'month', { key: string; stock: number }[]>;
}

export interface DashboardInbound {
  weeks: { key: string; start: string; inbound: number; source: 'wms' | 'vht' }[];
  /** Per week: the mean, the standard deviation, the lowest and the highest week. */
  mean: number;
  sd: number;
  low: number;
  high: number;
}

/** One owner in one month: what stood each day, summed, and over how many days. */
export interface DashboardBillingMonth {
  /** The first day counted: the 1st, or later when VHT began counting this owner then. */
  from: string;
  to: string;
  itemDays: number;
  days: number;
  average: number;
}

export interface DashboardBilling {
  /** This month up to and including today, and the whole of last month. */
  thisMonth: { key: string; start: string; end: string };
  lastMonth: { key: string; start: string; end: string };
  owners: {
    id: string;
    name: string;
    /** The party's debiteurcode. */
    code: string;
    shortName: string;
    /** The first day VHT counts this owner; null before its first booking. */
    since: string | null;
    /** Null when VHT did not count this owner yet in that month. */
    thisMonth: DashboardBillingMonth | null;
    lastMonth: DashboardBillingMonth | null;
  }[];
}

/** `owner`: the slug of the owner chosen; everything without. */
export const getStorageDashboard = (owner?: string) =>
  apiClient.get<StorageDashboard>(`${BASE}/dashboard`, { owner });

/** One tile: what came in, left and moved in a day, week or month (§20, step 6). */
export interface DashboardMovements {
  grain: 'day' | 'week' | 'month';
  period: string;
  start: string;
  end: string;
  in: number;
  out: number;
  /** Out and in again within seven days: not an arrival and not a departure. */
  moved: number;
  /** Only what moved in the period; the most first. */
  byType: {
    name: string;
    in: number;
    out: number;
    colorToken: StationColor | null;
    markLetter: string | null;
  }[];
  byPlan: { name: string; in: number; out: number }[];
}

export const getStorageMovements = (
  grain: DashboardMovements['grain'],
  period: string,
  owner?: string,
) => apiClient.get<DashboardMovements>(`${BASE}/dashboard/movements`, { grain, period, owner });

export const getMapConfig = () =>
  apiClient.get<{ apiKey: string | null; mapAvailable: boolean }>(`${BASE}/map-config`);

// ── Beheer (admin) ──────────────────────────────────────────────────────────

export interface FloorplanInput {
  name?: string;
  loadingPlaceId?: string | null;
  stationWidth?: number;
  stationHeight?: number;
  stationGap?: number;
  startX?: number;
  startY?: number;
  latitude?: number | null;
  longitude?: number | null;
}

export const createFloorplan = (input: FloorplanInput & { name: string }) =>
  apiClient.post<Floorplan>(`${BASE}/floorplans`, input);
export const updateFloorplan = (id: string, input: FloorplanInput) =>
  apiClient.patch<Floorplan>(`${BASE}/floorplans/${id}`, input);
export const deleteFloorplan = (id: string) => apiClient.delete<void>(`${BASE}/floorplans/${id}`);

/** The SVG as the raw body; the server checks and cleans it (drawing.ts). */
export const uploadDrawing = (id: string, file: File) =>
  putBinary<Floorplan>(`${BASE}/floorplans/${id}/drawing`, file, {
    'Content-Type': 'image/svg+xml',
    'X-VHT-File-Name': file.name,
  });

export const createStationType = (input: {
  name: string;
  ownerId: string;
  colorToken: StationColor;
  articleId?: string | null;
  adrUnNumber?: string | null;
}) => apiClient.post<StationType>(`${BASE}/station-types`, input);
export const updateStationType = (
  id: string,
  input: {
    name?: string;
    colorToken?: StationColor;
    articleId?: string | null;
    adrUnNumber?: string | null;
  },
) => apiClient.patch<StationType>(`${BASE}/station-types/${id}`, input);
export const deleteStationType = (id: string) =>
  apiClient.delete<void>(`${BASE}/station-types/${id}`);

export const createOwner = (input: {
  partyId: string;
  codeRule: StationCodeRule;
  hasCustomers: boolean;
  shortName?: string;
  markLetter?: string;
  wmsRelation?: string | null;
}) => apiClient.post<StorageOwner>(`${BASE}/owners`, input);
export const updateOwner = (
  id: string,
  input: {
    codeRule?: StationCodeRule;
    hasCustomers?: boolean;
    shortName?: string;
    markLetter?: string | null;
    wmsRelation?: string | null;
  },
) => apiClient.patch<StorageOwner>(`${BASE}/owners/${id}`, input);
export const deleteOwner = (id: string) => apiClient.delete<void>(`${BASE}/owners/${id}`);

/**
 * The size of an SVG the admin just picked, read in the browser for the preview —
 * the server reads it again and decides (drawing.ts).
 */
export function svgSize(text: string): { width: number; height: number } | null {
  const tag = /<svg\b[^>]*>/i.exec(text)?.[0];
  if (!tag) return null;
  const attr = (name: string) =>
    new RegExp(`\\s${name}\\s*=\\s*["']([^"']*)["']`, 'i').exec(tag)?.[1];
  const viewBox = attr('viewBox')
    ?.trim()
    .split(/[\s,]+/)
    .map(Number);
  if (viewBox && viewBox.length === 4 && viewBox.every(Number.isFinite)) {
    return { width: Math.round(viewBox[2]!), height: Math.round(viewBox[3]!) };
  }
  const w = Number(attr('width')?.replace(/px$/i, ''));
  const h = Number(attr('height')?.replace(/px$/i, ''));
  return Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0
    ? { width: Math.round(w), height: Math.round(h) }
    : null;
}

/** What the download is called, as the API names it: the export menu shows it (handoff §6.4). */
export const exportFileName = (owner?: Pick<StorageOwner, 'slug'>) =>
  owner ? `stations-${owner.slug}.xlsx` : 'stations.xlsx';

/**
 * The floorplanner's export, unchanged: `stations.xlsx` (docs/storage.md §2). For one
 * owner of several, only its stations and named after it (§21): `stations-alfen.xlsx`.
 * Returns the file name, for the toast.
 */
export async function exportStations(owner?: Pick<StorageOwner, 'id' | 'slug'>): Promise<string> {
  const file = exportFileName(owner);
  const query = owner ? `?owner=${encodeURIComponent(owner.id)}` : '';
  await downloadBinary(`${BASE}/stations/export.xlsx${query}`, file);
  return file;
}

export const createStation = (input: StationInput & { floorplanId: string }) =>
  apiClient.post<StorageStation>(`${BASE}/stations`, input);
export const updateStation = (
  id: string,
  input: StationInput & { x?: number; y?: number; rotation?: 0 | 90 },
) => apiClient.patch<StorageStation>(`${BASE}/stations/${id}`, input);
export const deleteStation = (id: string) => apiClient.delete<void>(`${BASE}/stations/${id}`);

/**
 * The drawing as an object URL, fetched through the API (with the session, or the
 * mock headers in development). `version` changes when the drawing is replaced.
 */
export function useDrawingUrl(floorplanId: string, version: string | null): string | null {
  const blob = useQuery({
    queryKey: ['storage', 'drawing', floorplanId, version],
    queryFn: () => fetchBlob(`${BASE}/floorplans/${floorplanId}/drawing`),
    enabled: version !== null,
    staleTime: Infinity,
  });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob.data) return;
    const next = URL.createObjectURL(blob.data);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob.data]);
  return url;
}

/**
 * The line under a plan's name: the loading place's address, else its short name or
 * name (the address only arrives with an export that carries it).
 */
export function placeLine(place: Floorplan['loadingPlace'], t: (key: string) => string): string {
  if (!place) return t('storage.noLoadingPlace');
  return place.address ?? place.shortName ?? place.name ?? '';
}

/** Station types the way people read them: Diabolo 20H … 40H, then Pacto 1, 2, 2T, 10, 20. */
export const byName = (a: { name: string }, b: { name: string }) =>
  a.name.localeCompare(b.name, 'nl', { numeric: true });

/** Matches the search on the plan: order number, the customer's code and name. */
export function matchesSearch(s: StorageStation, query: string): boolean {
  const q = query.trim().toUpperCase();
  if (!q) return true;
  return (
    s.code.toUpperCase().includes(q) ||
    (s.party !== null &&
      (s.party.code.toUpperCase().includes(q) || s.party.name.toUpperCase().includes(q)))
  );
}

// The overview's view (list or map), remembered for this tab: "‹ Plattegronden" on
// a plan goes back to the view it was opened from. A convenience, so it may fail.
const VIEW_KEY = 'vht:storage:overview-view';
export function rememberOverviewView(view: 'list' | 'map'): void {
  try {
    sessionStorage.setItem(VIEW_KEY, view);
  } catch {
    /* private mode or blocked storage: the list is a fine default */
  }
}
export function lastOverviewView(): 'map' | undefined {
  try {
    return sessionStorage.getItem(VIEW_KEY) === 'map' ? 'map' : undefined;
  } catch {
    return undefined;
  }
}
